import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Send, Home, AlertTriangle } from "lucide-react";

export default function CrmBuyers({ onCompose }) {
    const [items, setItems] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [chosen, setChosen] = useState({}); // lead_id -> property_id

    useEffect(() => {
        api.get("/crm/buyers-matching").then((r) => {
            setItems(r.data.items || []);
            const def = {};
            (r.data.items || []).forEach((b) => { if (b.matches[0]) def[b.lead_id] = b.matches[0].property_id; });
            setChosen(def);
        }).catch(() => setItems([]));
    }, []);

    if (items === null) return <div className="p-10 text-center text-ink-400 text-sm">Analisi matching in corso...</div>;

    const eligible = items.filter((b) => b.match_count > 0);
    const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleAll = () => setSelected((s) => s.size === eligible.length ? new Set() : new Set(eligible.map((b) => b.lead_id)));

    const compose = () => {
        const buyers = items.filter((b) => selected.has(b.lead_id)).map((b) => ({
            lead: b,
            property: b.matches?.find((m) => m.property_id === chosen[b.lead_id]) || b.matches?.[0],
        }));
        if (buyers.length === 0) return alert("Seleziona almeno un acquirente con immobili compatibili");
        onCompose(buyers);
    };

    return (
        <div data-testid="buyers-view">
            <p className="text-xs text-ink-400 mb-4">Acquirenti dal CRM con scheda esigenze: il matching esistente individua gli immobili compatibili in portafoglio. Seleziona chi contattare e conferma l&apos;immobile da proporre.</p>
            {selected.size > 0 && (
                <div data-testid="buyers-selection-bar" className="flex items-center gap-3 mb-4 p-3 border border-gold/40 bg-gold/5 text-xs">
                    <span className="text-gold font-medium">{selected.size} acquirenti selezionati</span>
                    <button data-testid="buyers-send-btn" onClick={compose} className="cta-gold px-4 py-1.5 flex items-center gap-2"><Send size={12} /> Proponi immobile</button>
                </div>
            )}
            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-ink-500 border-b border-white/10">
                            <th className="p-3 w-8"><input type="checkbox" data-testid="buyers-select-all" checked={eligible.length > 0 && selected.size === eligible.length} onChange={toggleAll} /></th>
                            <th className="p-3">Acquirente</th><th className="p-3">Contatti</th><th className="p-3">Agente</th>
                            <th className="p-3">Compatibili</th><th className="p-3">Immobile da proporre</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-ink-500">Nessun acquirente con scheda esigenze nel CRM. Compila i profili di ricerca nella pipeline.</td></tr>}
                        {items.map((b) => (
                            <tr key={b.lead_id} data-testid={`buyer-row-${b.lead_id}`} className={`border-b border-white/5 hover:bg-white/[0.03] ${b.match_count === 0 ? "opacity-45" : ""}`}>
                                <td className="p-3"><input type="checkbox" disabled={b.match_count === 0} checked={selected.has(b.lead_id)} onChange={() => toggle(b.lead_id)} /></td>
                                <td className="p-3 text-ink-50 font-medium">{b.name}</td>
                                <td className="p-3 text-ink-300">{b.email || "—"}<br />{b.phone || ""}</td>
                                <td className="p-3 text-ink-400">{b.agent || "—"}</td>
                                <td className="p-3">
                                    {b.match_count > 0
                                        ? <span data-testid={`buyer-matches-${b.lead_id}`} className="px-2 py-0.5 border border-green-700/40 text-green-300 bg-green-900/10 text-[10px]"><Home size={9} className="inline mr-1" />{b.match_count} immobili</span>
                                        : <span data-testid={`buyer-nomatch-${b.lead_id}`} className="px-2 py-0.5 border border-orange-700/40 text-orange-300 bg-orange-900/10 text-[10px]"><AlertTriangle size={9} className="inline mr-1" />Nessun immobile compatibile</span>}
                                </td>
                                <td className="p-3">
                                    {b.match_count > 0 ? (
                                        <select data-testid={`buyer-prop-select-${b.lead_id}`} value={chosen[b.lead_id] || ""} onChange={(e) => setChosen((c) => ({ ...c, [b.lead_id]: e.target.value }))}
                                            className="bg-ink-950 border border-white/15 px-2 py-1.5 text-[11px] text-ink-100 outline-none max-w-[280px]">
                                            {(b.matches || []).map((m) => <option key={m.property_id} value={m.property_id}>{m.title} · {m.score}% match</option>)}
                                        </select>
                                    ) : <span className="text-ink-600">escluso dall&apos;invio</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
